import { createProviderToken } from '@core/common-legacy';

/**
 * Optional contract controlling where report exports (PNG capture) are rendered.
 *
 * PNG export renders a dedicated visualization component off-screen and captures
 * it with `html-to-image`, which serializes the computed styles of the live DOM
 * nodes. By default the render is attached to `document.body`, which is fine for
 * apps whose global/theme styles live in the document head (e.g. Recruitee).
 *
 * Apps that scope their styles to shadow roots (e.g. Tellent/Kiwi via
 * `adoptedStyleSheets`) must return a render root inside such a shadow root —
 * otherwise the detached render is unstyled and the capture comes out blank or
 * mislaid out (e.g. blank charts, missing card backgrounds).
 *
 * Returning a node inside a shadow root is safe only because the export
 * service creates its component with a detached host element and attaches it
 * to this root afterwards. Since Angular 22, `createComponent` with a host
 * element already inside a shadow root registers that shadow root as a
 * SharedStylesHost style host and, on destroy, strips all framework-managed
 * styles from it — fatal for a shared root like the one hosting the CDK
 * overlay container. Prefer a dedicated element over CDK-managed DOM (e.g. a
 * sibling of `.cdk-overlay-container`, not the container itself).
 */
export interface ReportsExportRenderHost {
  getRenderRoot: () => HTMLElement;
}

export const { inject: injectReportsExportRenderHost, provide: provideReportsExportRenderHost } =
  createProviderToken<ReportsExportRenderHost>('ReportsExportRenderHost')();
